import React, { useCallback, useMemo } from "react";

import {
    AppBar,
    Authenticator,
    CircularProgressCenter,
    Drawer,
    FireCMS,
    ModeControllerProvider,
    NavigationRoutes,
    Scaffold,
    SideDialogs,
    SnackbarProvider,
    useBuildLocalConfigurationPersistence,
    useBuildModeController,
    useBuildNavigationController,
    useValidateAuthenticator
} from "@firecms/core";
import {
    FirebaseAuthController,
    FirebaseLoginView,
    FirebaseSignInProvider,
    FirebaseUserWrapper,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
    useInitialiseFirebase,
} from "@firecms/firebase";
import { getAuth } from "firebase/auth";
import { CenteredView } from "@firecms/ui";

import { directoryCollection } from "./collections/directory";
import { listiclePagesCollection } from "./collections/listicle_pages";
import { profileCollection } from "./collections/profile";
import { fieldCollection } from "./collections/field";
import { userCollection } from "./collections/user";
import { locationsCollection } from "./collections/locations";

import { firebaseConfig } from "./firebase_config";

function App() {

    // Use your own authentication logic here
    const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(async ({
        user,
        authController
    }) => {

        if (user?.email?.includes("flanders")) {
            // You can throw an error to prevent access
            throw Error("Stupid Flanders!");
        }

        const idTokenResult = await user?.firebaseUser?.getIdTokenResult();
        const userIsAdmin = idTokenResult?.claims.admin || user?.email?.endsWith("@firecms.co");
        if (userIsAdmin) {
            console.log("User is admin, allowing access");
            return true;
        }
        console.log("Access deniedl: user is not admin");

        // we allow access to every user in this case
        return false;
    }, []);

    const collections = useMemo(() => [
        directoryCollection,
        listiclePagesCollection,
        profileCollection,
        userCollection,
        fieldCollection,
        locationsCollection
    ], []);

    const {
        firebaseApp,
        firebaseConfigLoading,
        configError
    } = useInitialiseFirebase({
        firebaseConfig,
        onFirebaseInit: (config, app) => {
            if ("tenantId" in config && typeof config.tenantId === "string") {
                const auth = getAuth(app);
                auth.tenantId = config.tenantId;
                console.log("Setting tenantId", config.tenantId);
            }
        }
    });


    // Controller used to manage the dark or light color mode
    const modeController = useBuildModeController();

    const signInOptions: FirebaseSignInProvider[] = ["google.com"];

    // Controller for managing authentication
    const authController: FirebaseAuthController = useFirebaseAuthController({
        firebaseApp,
        signInOptions
    });

    // Controller for saving some user preferences locally.
    const userConfigPersistence = useBuildLocalConfigurationPersistence();

    // Delegate used for fetching and saving data in Firestore
    const firestoreDelegate = useFirestoreDelegate({
        firebaseApp
    });

    // Controller used for saving and fetching files in storage
    const storageSource = useFirebaseStorageSource({
        firebaseApp
    });

    const {
        authLoading,
        canAccessMainView,
        notAllowedError
    } = useValidateAuthenticator({
        authController,
        authenticator: myAuthenticator,
        dataSourceDelegate: firestoreDelegate,
        storageSource
    });

    const navigationController = useBuildNavigationController({
        disabled: authLoading,
        collections,
        authController,
        dataSourceDelegate: firestoreDelegate
    });

    if (firebaseConfigLoading || !firebaseApp) {
        return <>
            <CircularProgressCenter />
        </>;
    }

    if (configError) {
        return <CenteredView>{configError}</CenteredView>;
    }

    return (
        <SnackbarProvider>
            <ModeControllerProvider value={modeController}>
                <FireCMS
                    navigationController={navigationController}
                    authController={authController}
                    userConfigPersistence={userConfigPersistence}
                    dataSourceDelegate={firestoreDelegate}
                    storageSource={storageSource}
                >
                    {({
                        context,
                        loading
                    }) => {

                        if (loading || authLoading) {
                            return <CircularProgressCenter size={"large"} />;
                        }

                        if (!canAccessMainView) {
                            return <FirebaseLoginView authController={authController}
                                firebaseApp={firebaseApp}
                                signInOptions={signInOptions}
                                logo={"/assets/app_launcher_icon.png"}
                                notAllowedError={notAllowedError} />;
                        }

                        return <Scaffold
                            autoOpenDrawer={false}
                            logo={"/assets/app_launcher_icon.png"}>
                            <AppBar title={"Digital Reference CMS"} />
                            <Drawer />
                            <NavigationRoutes />
                            <SideDialogs />
                        </Scaffold>;
                    }}
                </FireCMS>
            </ModeControllerProvider>
        </SnackbarProvider>
    );

}

export default App;
